/**
 * Service Clusters API client (UI Redesign).
 *
 * Clusters are the persistent, service-shaped home for a client's keywords —
 * they replace the per-seed "Completed Sessions" list on Keyword Research.
 *
 * ⚠️ Every metric is `number | null`. `null` means "we could not measure this",
 * NOT zero. Render an em dash — see `formatMetric`. The backend used to return
 * volume 0 / difficulty 999 on a failed Ahrefs lookup, which made good keywords
 * look worthless; that is fixed and must not creep back in on the client side.
 */

import { apiClient } from './apiClient';

export type ClusterSource = 'brand_bible' | 'manual' | 'ai';
export type ClusterStatus = 'active' | 'exhausted' | 'archived';
export type KeywordStatus = 'suggested' | 'approved' | 'rejected' | 'queued' | 'used';
export type MetricsStatus = 'pending' | 'ok' | 'unavailable';

export interface ServiceClusterKeyword {
  id: string;
  cluster_id: string;
  keyword: string;
  search_volume: number | null;
  keyword_difficulty: number | null;
  cpc: number | null;
  intent: string | null;
  metrics_status: MetricsStatus;
  metrics_error: string | null;
  metrics_fetched_at: string | null;
  status: KeywordStatus;
  used_content_id: string | null;
  used_at: string | null;
  rationale: string | null;
  /** Why this keyword was rejected (feeds AI Suggest's negative examples). */
  rejection_reason: string | null;
  source: 'ai_suggest' | 'manual' | 'import';
  created_at: string;
  updated_at: string;
}

export interface ClusterCounts {
  suggested: number;
  approved: number;
  rejected: number;
  queued: number;
  used: number;
}

export interface ServiceCluster {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  source: ClusterSource;
  status: ClusterStatus;
  sort_order: number;
  last_suggested_at: string | null;
  created_at: string;
  updated_at: string;
  counts: ClusterCounts;
  /** Only populated by the detail endpoint. */
  keywords: ServiceClusterKeyword[];
}

export interface ServiceClusterListResponse {
  clusters: ServiceCluster[];
  total: number;
  exhausted_cluster_ids: string[];
}

export interface SuggestKeywordsResponse {
  cluster_id: string;
  created: ServiceClusterKeyword[];
  duplicates_skipped: string[];
  metrics_unavailable: number;
}

export interface AddKeywordsResponse {
  created: ServiceClusterKeyword[];
  duplicates_skipped: string[];
}

export interface SeedFromBrandBibleResponse {
  created: ServiceCluster[];
  skipped: string[];
  services_found: number;
}

export interface BulkDeleteResponse {
  deleted: number;
  failed: string[];
}

export interface BulkSuggestResult {
  cluster_id: string;
  created: number;
  duplicates_skipped: number;
  metrics_unavailable: number;
  error: string | null;
}

export interface BulkSuggestResponse {
  results: BulkSuggestResult[];
}

export interface ExhaustionCluster {
  cluster_id: string;
  name: string;
  approved_remaining: number;
  used_total: number;
}

export interface ExhaustionReport {
  tenant_id: string;
  exhausted: ExhaustionCluster[];
  running_low: ExhaustionCluster[];
  total_approved_remaining: number;
  low_threshold: number;
}

/** Keywords that are available to write about right now. */
export const availableCount = (counts: ClusterCounts): number => counts.approved;

/** Keywords that have been spent — queued or already written. */
export const usedCount = (counts: ClusterCounts): number => counts.queued + counts.used;

/**
 * Format a metric for display. `null` → em dash, never "0" and never "999".
 */
export function formatMetric(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
}

const baseKey = ['service-clusters'] as const;

export const serviceClusterQueryKeys = {
  all: baseKey,
  list: (tenantId: string, includeArchived = false) =>
    [...baseKey, 'list', tenantId, includeArchived] as const,
  detail: (clusterId: string | null) => [...baseKey, 'detail', clusterId] as const,
  exhaustion: (tenantId: string) => [...baseKey, 'exhaustion', tenantId] as const,
};

export const serviceClusterService = {
  async list(
    tenantId: string,
    includeArchived = false,
  ): Promise<ServiceClusterListResponse> {
    const { data } = await apiClient.get<ServiceClusterListResponse>('/service-clusters', {
      params: { tenant_id: tenantId, include_archived: includeArchived },
    });
    return data;
  },

  async get(tenantId: string, clusterId: string): Promise<ServiceCluster> {
    const { data } = await apiClient.get<ServiceCluster>(
      `/service-clusters/${clusterId}`,
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async create(
    tenantId: string,
    payload: { name: string; description?: string; source?: ClusterSource },
  ): Promise<ServiceCluster> {
    const { data } = await apiClient.post<ServiceCluster>('/service-clusters', payload, {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async update(
    tenantId: string,
    clusterId: string,
    payload: { name?: string; description?: string; status?: ClusterStatus },
  ): Promise<ServiceCluster> {
    const { data } = await apiClient.patch<ServiceCluster>(
      `/service-clusters/${clusterId}`,
      payload,
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async remove(tenantId: string, clusterId: string): Promise<void> {
    await apiClient.delete(`/service-clusters/${clusterId}`, {
      params: { tenant_id: tenantId },
    });
  },

  async seedFromBrandBible(tenantId: string): Promise<SeedFromBrandBibleResponse> {
    const { data } = await apiClient.post<SeedFromBrandBibleResponse>(
      '/service-clusters/seed-from-brand-bible',
      { skip_existing: true },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async suggest(
    tenantId: string,
    clusterId: string,
    limit = 25,
  ): Promise<SuggestKeywordsResponse> {
    const { data } = await apiClient.post<SuggestKeywordsResponse>(
      `/service-clusters/${clusterId}/suggest`,
      { limit, fetch_metrics: true },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async addKeywords(
    tenantId: string,
    clusterId: string,
    keywords: string[],
    status: KeywordStatus = 'approved',
  ): Promise<AddKeywordsResponse> {
    const { data } = await apiClient.post<AddKeywordsResponse>(
      `/service-clusters/${clusterId}/keywords`,
      { keywords, status, fetch_metrics: true },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async setKeywordStatus(
    tenantId: string,
    clusterId: string,
    keywordIds: string[],
    status: KeywordStatus,
    reason?: string | null,
  ): Promise<{ updated: ServiceClusterKeyword[]; cluster_status: ClusterStatus }> {
    const { data } = await apiClient.patch(
      `/service-clusters/${clusterId}/keywords/status`,
      { keyword_ids: keywordIds, status, reason: reason ?? null },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  /** Delete several clusters at once. Returns how many went + which ids failed. */
  async bulkDelete(tenantId: string, clusterIds: string[]): Promise<BulkDeleteResponse> {
    const { data } = await apiClient.post<BulkDeleteResponse>(
      '/service-clusters/bulk-delete',
      { cluster_ids: clusterIds },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  /** Run AI Suggest across several clusters in one call (per-cluster results). */
  async bulkSuggest(
    tenantId: string,
    clusterIds: string[],
    limit = 25,
  ): Promise<BulkSuggestResponse> {
    const { data } = await apiClient.post<BulkSuggestResponse>(
      '/service-clusters/bulk-suggest',
      { cluster_ids: clusterIds, limit, fetch_metrics: true },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async exhaustion(tenantId: string): Promise<ExhaustionReport> {
    const { data } = await apiClient.get<ExhaustionReport>(
      '/service-clusters/exhaustion',
      { params: { tenant_id: tenantId } },
    );
    return data;
  },
};
